import { Callout } from './components/callout.tsx'
import { PaidFeature } from './components/paid-feature.tsx'
import { Xcelera } from './components/xcelera.tsx'

export const meta = () => [
	{ title: 'Regions & Devices · xcelera docs' },
	{
		name: 'description',
		content:
			'Where xcelera audits run and what they emulate: 22 regions across six continents, and mobile and desktop device profiles with real packet-level network throttling.',
	},
]

# Regions & Devices

Every page you track audits from a fixed region on a fixed device profile. You set both when you add the page (new pages start from your [organization defaults](#defaults)), and every subsequent audit uses them, so results stay comparable run over run.

## Device profiles

A profile determines the emulated hardware and network conditions for the audit. We currently offer two fixed profiles:

|                 | Mobile                         | Desktop           |
| --------------- | ------------------------------ | ----------------- |
| Viewport        | 412 × 823 (mid-range phone)    | 1350 × 940        |
| Download        | 1.6 Mbit/s                     | 10 Mbit/s         |
| Upload          | 750 kbit/s                     | 10 Mbit/s         |
| Round-trip time | 150 ms                         | 40 ms             |
| CPU             | Throttled to a mid-range phone | Lightly throttled |

These match Lighthouse's standard mobile (slow 4G) and desktop (broadband) conditions, so scores line up with what PageSpeed Insights would report for the same page.

<Callout type="info">
	Unlike most Lighthouse services, <Xcelera /> applies these network conditions
	for real, with packet-level shaping rather than simulation. See [packet-level
	network shaping](/docs/audit-stability#packet-level-network-shaping) on the
	[Audit Stability](/docs/audit-stability) page for how this works and why it
	makes results more accurate.
</Callout>

Mobile is the default. The same URL on mobile and on desktop is two separate pages, each with its own audit history and schedule, so to track both, add the page twice.

## Choosing a region <PaidFeature />

Audits run from the region saved on the page. Free organizations run from Eastern US (South Carolina); paid plans choose from any of the 22 regions below.

Pick the region closest to your users, not your servers. The device profile is identical in every region, so any difference between auditing from Tokyo and auditing from Belgium is real network distance between that region and your origin or CDN. If your customers are in Japan and your audits from Tokyo are slower than your audits from Iowa, your Japanese users feel that same gap.

Then keep it fixed. A trend only means something when every point measures the same thing from the same place.

## Available regions

**North America** — Central US (Iowa), Eastern US (South Carolina), Southern US (Dallas), Western US (Las Vegas), Mexico (Querétaro)

**South America** — Brazil (São Paulo), Chile (Santiago)

**Europe** — Belgium, Finland, France (Paris), Germany (Frankfurt), Spain (Madrid), UK (London)

**Middle East** — Israel (Tel Aviv), Qatar (Doha)

**Africa** — South Africa (Johannesburg)

**Asia Pacific** — Australia (Sydney), India (Mumbai), Japan (Tokyo), Singapore, South Korea (Seoul), Taiwan

## Defaults

**Settings → General** holds your organization's default region and device profile. New pages start from these; changing them affects only pages you add afterwards.

## Changing region or device on an existing page

You can edit a page's device profile any time, and its region on a paid plan.

<Callout type="warning">
	Audits before and after the change measure different things. A desktop score
	isn't comparable to a mobile score, and a page served to São Paulo isn't the
	page served to London. Treat the history as starting over. To keep the old
	trend intact, leave the page as it is and add a second page with the new
	settings.
</Callout>

## Next steps

- [Getting Started](/docs/getting-started) — add a page and run your first audit
- [Audit Stability](/docs/audit-stability) — how <Xcelera /> produces measurements stable enough to compare
- [Usage & Billing](/docs/usage-and-billing) — which plans include region selection
